import type { EntityManager } from './entityManager';

export interface ActionHistoryEntry {
  action: any; // Serialized action in relative coordinate format
  snapshotAfter: string; // Serialized EntityManager state (JSON)
  timestamp: number;
}

/**
 * Manages action history and snapshots for undo/replay functionality.
 *
 * Responsibilities:
 * - Track player actions with timestamps
 * - Capture entity state snapshots after each action
 * - Restore previous states for undo functionality
 * - Provide action history for solution saving/replay
 *
 * Architecture:
 * - Pure state management (no event dependencies, no rendering dependencies)
 * - Returns results synchronously for LevelManager to handle
 * - LevelManager is responsible for emitting events based on state changes
 */
export default class ActionHistory {
  private actionHistory: ActionHistoryEntry[];
  private initialSnapshot: string | null;
  private pendingAction: any | null;
  private undoCount: number;
  private entityManager: EntityManager;

  constructor(entityManager: EntityManager) {
    this.entityManager = entityManager;
    this.actionHistory = [];
    this.initialSnapshot = null;
    this.pendingAction = null;
    this.undoCount = 0;
  }

  /**
   * Initialize for a new level - capture initial state.
   * Call this after level data is loaded into entityManager.
   */
  init(): void {
    this.actionHistory = [];
    this.pendingAction = null;
    this.undoCount = 0;
    this.initialSnapshot = this.entityManager.serialize();
  }

  /**
   * Record an action before resolution.
   * The action is stored in pending state until committed after resolution.
   *
   * @param action - Serialized action in relative coordinate format
   */
  recordPendingAction(action: any): void {
    this.pendingAction = action;
  }

  /**
   * Commit the pending action with a snapshot after resolution completes.
   * This is called by LevelManager after action resolution and animations finish.
   * Only commits if there is a pending action.
   */
  commitPendingAction(): void {
    if (!this.pendingAction) return;

    const snapshotAfter = this.entityManager.serialize();
    this.actionHistory.push({
      action: this.pendingAction,
      snapshotAfter,
      timestamp: Date.now(),
    });
    this.pendingAction = null;
  }

  /**
   * Undo the last action by restoring the previous snapshot.
   *
   * @returns Set of affected entity IDs for rendering updates, or null if no history
   */
  undoLastAction(): Set<number> | null {
    if (this.actionHistory.length === 0) {
      return null; // Nothing to undo
    }

    // Track that an undo occurred
    this.undoCount++;

    // Remove last action from history
    this.actionHistory.pop();

    // Determine which snapshot to restore
    const snapshotToRestore = this.actionHistory.length === 0
      ? this.initialSnapshot!
      : this.actionHistory[this.actionHistory.length - 1].snapshotAfter;

    // Restore the snapshot
    const affectedEntityIds = this.restoreSnapshot(snapshotToRestore);

    return affectedEntityIds;
  }

  /**
   * Restore a snapshot and return affected entity IDs.
   * This handles the complex logic of syncing entity state with the snapshot,
   * including recreating destroyed entities and removing extra entities.
   *
   * @param snapshot - Serialized EntityManager state to restore
   * @returns Set of entity IDs that were affected (created, modified, or destroyed)
   */
  private restoreSnapshot(snapshot: string): Set<number> {
    const affectedEntityIds = new Set<number>();

    // Parse snapshot into temporary EntityManager
    const EntityManagerClass = this.entityManager.constructor as typeof EntityManager;
    const tempEntityManager = new EntityManagerClass();
    tempEntityManager.resetAndLoad(snapshot);

    const snapshotEntityIds = new Set<number>();

    // Update/recreate entities to match snapshot
    tempEntityManager.query([]).forEach((snapshotEntity: any) => {
      snapshotEntityIds.add(snapshotEntity.id);
      affectedEntityIds.add(snapshotEntity.id);

      let entity = this.entityManager.getEntity(snapshotEntity.id);

      if (!entity) {
        // Entity was destroyed (e.g., by CleanupSystem) - recreate it
        const { $loki, meta, ...snapshotDoc } = snapshotEntity.doc;
        // Access private entities field (used by existing code in LevelManager)
        const newRecord = (this.entityManager as any).entities.insert({ ...snapshotDoc });
        affectedEntityIds.add(newRecord.$loki);
        snapshotEntityIds.add(newRecord.$loki);
      } else {
        // Entity exists - sync all components
        this.syncEntityComponents(entity.doc, snapshotEntity.doc);
      }
    });

    // Remove entities that exist in current state but not in snapshot
    this.entityManager.query([]).forEach((entity: any) => {
      if (!snapshotEntityIds.has(entity.id)) {
        entity.destroy();
        affectedEntityIds.add(entity.id);
      }
    });

    return affectedEntityIds;
  }

  /**
   * Sync all components from snapshot entity to current entity.
   * This copies component data and removes components that don't exist in the snapshot.
   *
   * @param entityDoc - Current entity's document
   * @param snapshotDoc - Snapshot entity's document
   */
  private syncEntityComponents(entityDoc: any, snapshotDoc: any): void {
    // Copy all components from snapshot to entity
    for (const key in snapshotDoc) {
      // Skip metadata fields
      if (key === 'id' || key === '$loki' || key === 'meta') continue;

      const snapshotComponent = snapshotDoc[key];
      if (!snapshotComponent) continue;

      if (typeof entityDoc[key] === "object") {
        // Component exists in both - update it
        Object.assign(entityDoc[key], snapshotComponent);
      } else {
        // Component only in snapshot - add it
        entityDoc[key] = snapshotComponent;
      }
    }

    // Remove components that exist in entity but not in snapshot
    for (const key in entityDoc) {
      if (key === 'id' || key === '$loki' || key === 'meta') continue;
      if (!snapshotDoc[key]) {
        delete entityDoc[key];
      }
    }
  }

  // ==================== Public Accessor Methods ====================

  /**
   * Get the current move count.
   * @returns Number of actions in history
   */
  getMoveCount(): number {
    return this.actionHistory.length;
  }

  /**
   * Get the current undo count.
   * @returns Number of times undo was used
   */
  getUndoCount(): number {
    return this.undoCount;
  }

  /**
   * Get a copy of the action history.
   * @returns Copy of action history array
   */
  getActionHistory(): ActionHistoryEntry[] {
    return [...this.actionHistory];
  }

  /**
   * Get a snapshot by index.
   * @param index - Index in action history (-1 for initial snapshot, 0+ for action snapshots)
   * @returns Serialized snapshot or null if invalid index
   */
  getSnapshot(index: number): string | null {
    if (index === -1) return this.initialSnapshot;
    if (index >= 0 && index < this.actionHistory.length) {
      return this.actionHistory[index].snapshotAfter;
    }
    return null;
  }

  /**
   * Get the player's solution (action sequence).
   * This is used for saving/comparing solutions.
   * @returns Array of actions in relative coordinate format
   */
  getPlayerSolution(): any[] {
    return this.actionHistory.map(entry => entry.action);
  }

  /**
   * Clear all action history and snapshots.
   * Called when level ends or is restarted.
   */
  clear(): void {
    this.actionHistory = [];
    this.initialSnapshot = null;
    this.pendingAction = null;
    this.undoCount = 0;
  }
}
